import { logger } from "../utils/logger";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { Calculator, Download, TrendingUp, Users, DollarSign, Clock, CheckCircle, Target, Sparkles, ChevronDown, ChevronUp, BarChart3, PieChart, ArrowRight, Zap, Award, TrendingDown, X, Info, AlertCircle, Video, Megaphone, UserCheck } from "lucide-react";
import { apiFetch, safeJsonParse, roiPageAPI } from '../utils/api';

interface FormData {
  // Core Inputs
  industry: string;
  numVideos: number;
  avgCostPerVideo: number;
  employeesImpacted: number;
  avgHourlyRate: number;
  
  // Advanced Inputs
  hoursPerEmployeeSaved: number;
  baselineCloseRate: number;
  salesLeadsImpacted: number;
  conversionLift: number;
  avgDealSize: number;
  supportTicketsAvoided: number;
  avgTicketCost: number;
  contentLifespan: number;
}

interface LeadFormData {
  name: string;
  company: string;
  email: string;
  phone: string;
}

const industryPresets = {
  manufacturing: {
    name: "Manufacturing",
    numVideos: 6,
    avgCostPerVideo: 3200,
    employeesImpacted: 120,
    avgHourlyRate: 58,
    hoursPerEmployeeSaved: 2,
    baselineCloseRate: 8,
    salesLeadsImpacted: 120,
    conversionLift: 15,
    avgDealSize: 95000,
    supportTicketsAvoided: 60,
    avgTicketCost: 95,
    contentLifespan: 3,
  },
  energy: {
    name: "Energy & Utilities",
    numVideos: 7,
    avgCostPerVideo: 3800,
    employeesImpacted: 150,
    avgHourlyRate: 72,
    hoursPerEmployeeSaved: 2.5,
    baselineCloseRate: 7,
    salesLeadsImpacted: 80,
    conversionLift: 18,
    avgDealSize: 420000,
    supportTicketsAvoided: 50,
    avgTicketCost: 115,
    contentLifespan: 4,
  },
  automotive: {
    name: "Automotive",
    numVideos: 8,
    avgCostPerVideo: 2800,
    employeesImpacted: 250,
    avgHourlyRate: 52,
    hoursPerEmployeeSaved: 1.5,
    baselineCloseRate: 10,
    salesLeadsImpacted: 180,
    conversionLift: 12,
    avgDealSize: 75000,
    supportTicketsAvoided: 80,
    avgTicketCost: 85,
    contentLifespan: 2,
  },
  healthcare: {
    name: "Healthcare",
    numVideos: 6,
    avgCostPerVideo: 4200,
    employeesImpacted: 160,
    avgHourlyRate: 78,
    hoursPerEmployeeSaved: 2.5,
    baselineCloseRate: 9,
    salesLeadsImpacted: 90,
    conversionLift: 20,
    avgDealSize: 180000,
    supportTicketsAvoided: 70,
    avgTicketCost: 110,
    contentLifespan: 2,
  },
  aerospace: {
    name: "Aerospace",
    numVideos: 5,
    avgCostPerVideo: 5500,
    employeesImpacted: 140,
    avgHourlyRate: 88,
    hoursPerEmployeeSaved: 3,
    baselineCloseRate: 6,
    salesLeadsImpacted: 45,
    conversionLift: 20,
    avgDealSize: 850000,
    supportTicketsAvoided: 40,
    avgTicketCost: 125,
    contentLifespan: 4,
  },
  agriculture: {
    name: "Agriculture",
    numVideos: 6,
    avgCostPerVideo: 2900,
    employeesImpacted: 100,
    avgHourlyRate: 48,
    hoursPerEmployeeSaved: 2,
    baselineCloseRate: 8,
    salesLeadsImpacted: 110,
    conversionLift: 14,
    avgDealSize: 125000,
    supportTicketsAvoided: 55,
    avgTicketCost: 90,
    contentLifespan: 3,
  },
  banking: {
    name: "Banking & Finance",
    numVideos: 7,
    avgCostPerVideo: 4500,
    employeesImpacted: 180,
    avgHourlyRate: 68,
    hoursPerEmployeeSaved: 2.5,
    baselineCloseRate: 7,
    salesLeadsImpacted: 95,
    conversionLift: 18,
    avgDealSize: 280000,
    supportTicketsAvoided: 75,
    avgTicketCost: 105,
    contentLifespan: 2,
  },
  custom: {
    name: "Custom",
    numVideos: 6,
    avgCostPerVideo: 3500,
    employeesImpacted: 150,
    avgHourlyRate: 65,
    hoursPerEmployeeSaved: 2,
    baselineCloseRate: 8,
    salesLeadsImpacted: 100,
    conversionLift: 15,
    avgDealSize: 120000,
    supportTicketsAvoided: 60,
    avgTicketCost: 100,
    contentLifespan: 3,
  }
};

// ============================================================
// SliderField Component - Moved outside to prevent recreation
// ============================================================
function SliderField({
  label,
  value,
  onChange,
  prefix = '',
  suffix = '',
  tooltip = '',
  proTip = '',
  min = 0,
  max = 100,
  step = 1
}: {
  label: string;
  value: number;
  onChange: (val: number) => void;
  prefix?: string;
  suffix?: string;
  tooltip?: string;
  proTip?: string;
  min?: number;
  max?: number;
  step?: number;
}) {
  const [showProTip, setShowProTip] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value.toLocaleString());
  const [sliderValue, setSliderValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isTypingRef = useRef(false);
  
  useEffect(() => {
    const isInputFocused = inputRef.current === document.activeElement;
    if (!isInputFocused && !isFocused && !isDragging && !isTypingRef.current) {
      setLocalValue(value.toLocaleString());
      setSliderValue(value);
    }
  }, [value, isFocused, isDragging]);
  
  const formatDisplayValue = (val: number) => {
    let formatted = val.toLocaleString();
    if (prefix) formatted = prefix + formatted;
    if (suffix) formatted = formatted + suffix;
    return formatted;
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isTypingRef.current = true;
    const rawValue = e.target.value.replace(/[^0-9.]/g, '');
    setLocalValue(rawValue);
  };
  
  const handleBlur = () => {
    isTypingRef.current = false;
    setTimeout(() => {
      setIsFocused(false);
      const numValue = parseFloat(localValue.replace(/[^0-9.]/g, '') || '0');
      const clampedValue = Math.max(min, Math.min(max, numValue));
      onChange(clampedValue);
      setLocalValue(clampedValue.toLocaleString());
      setSliderValue(clampedValue);
    }, 0);
  };
  
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    isTypingRef.current = true;
    setIsFocused(true);
    const numValue = parseFloat(localValue.replace(/[^0-9.]/g, '') || '0');
    setLocalValue(numValue.toString());
    setTimeout(() => e.target.select(), 0);
  };
  
  return (
    <div>
      <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
        {label}
        {proTip && (
          <div className="relative">
            <button
              type="button"
              onMouseEnter={() => setShowProTip(true)}
              onMouseLeave={() => setShowProTip(false)}
              onClick={() => setShowProTip(!showProTip)}
              className="w-4 h-4 rounded-full bg-[#E87D3E]/10 hover:bg-[#E87D3E]/20 flex items-center justify-center transition-all group"
            >
              <Info className="w-3 h-3 text-[#E87D3E] group-hover:scale-110 transition-transform" />
            </button>
            
            {showProTip && (
              <div className="absolute left-0 top-6 z-50 w-64 sm:w-72 bg-white/95 backdrop-blur-xl rounded-xl shadow-2xl border-2 border-[#E87D3E]/20 p-4 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="flex items-start gap-2 mb-2">
                  <div className="w-6 h-6 bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="text-xs font-bold text-[#2F2E5C]">Pro Tip</div>
                </div>
                <p className="text-xs text-gray-700 leading-relaxed">{proTip}</p>
                <div className="absolute -top-2 left-4 w-4 h-4 bg-white/95 border-l-2 border-t-2 border-[#E87D3E]/20 rotate-45" />
              </div>
            )}
          </div>
        )}
      </label>
      
      <div className="space-y-3">
        <div className="relative group">
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={sliderValue}
            onMouseDown={() => setIsDragging(true)}
            onMouseUp={() => {
              setIsDragging(false);
              onChange(sliderValue);
            }}
            onTouchStart={() => setIsDragging(true)}
            onTouchEnd={() => {
              setIsDragging(false);
              onChange(sliderValue);
            }}
            onChange={(e) => {
              const newValue = parseFloat(e.target.value);
              setSliderValue(newValue);
              if (!isFocused) {
                setLocalValue(newValue.toLocaleString());
                onChange(newValue);
              }
            }}
            title={tooltip}
            className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer premium-slider"
            style={{
              background: `linear-gradient(to right, #E87D3E 0%, #E87D3E ${((sliderValue - min) / (max - min)) * 100}%, #e5e7eb ${((sliderValue - min) / (max - min)) * 100}%, #e5e7eb 100%)`
            }}
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{formatDisplayValue(min)}</span>
            <span>{formatDisplayValue(max)}</span>
          </div>
        </div>
        
        <div className="relative group">
          {prefix && (
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#2F2E5C] font-bold z-10 pointer-events-none">
              {prefix}
            </span>
          )}
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={localValue}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleInputChange}
            title={tooltip}
            className={`w-full ${prefix ? 'pl-9' : 'pl-4'} ${suffix ? 'pr-16' : 'pr-4'} py-3.5 bg-white/70 backdrop-blur-sm border-2 border-gray-200 rounded-xl text-base font-bold text-[#2F2E5C] text-center focus:border-[#E87D3E] focus:outline-none focus:ring-2 focus:ring-[#E87D3E]/20 focus:bg-white transition-all hover:border-gray-300 hover:shadow-md`}
          />
          {suffix && (
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-semibold pointer-events-none">
              {suffix}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function ROICalculator() {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    industry: 'manufacturing',
    ...industryPresets.manufacturing
  });

  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState<LeadFormData>({
    name: '',
    company: '',
    email: '',
    phone: ''
  });

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<{ email: string; reportId: string; name: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: keyof FormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleIndustryChange = (industry: string) => {
    const preset = industryPresets[industry as keyof typeof industryPresets];
    setFormData({
      industry,
      ...preset
    });
  };

  const handleLeadInputChange = (field: keyof LeadFormData, value: string) => {
    setLeadData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDownloadPDF = async () => {
    if (!leadData.name || !leadData.email) {
      setShowLeadForm(true);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submissionData = {
        form_type: 'roi-calculator',
        form_name: 'ROI Calculator Results',
        name: leadData.name,
        company: leadData.company,
        email: leadData.email,
        phone: leadData.phone,
        industry: formData.industry,
        numVideos: formData.numVideos,
        avgCostPerVideo: formData.avgCostPerVideo,
        employeesImpacted: formData.employeesImpacted,
        avgHourlyRate: formData.avgHourlyRate,
        hoursPerEmployeeSaved: formData.hoursPerEmployeeSaved,
        baselineCloseRate: formData.baselineCloseRate,
        salesLeadsImpacted: formData.salesLeadsImpacted,
        conversionLift: formData.conversionLift,
        avgDealSize: formData.avgDealSize,
        supportTicketsAvoided: formData.supportTicketsAvoided,
        avgTicketCost: formData.avgTicketCost,
        contentLifespan: formData.contentLifespan,
        totalInvestment,
        trainingSavings,
        salesRevenue,
        supportSavings,
        year1Benefits,
        year1ROI,
        year1ROIPercent: year1ROIPercent.toFixed(0),
        lifetimeValue,
        lifetimeROI,
        lifetimeROIPercent: lifetimeROIPercent.toFixed(0),
        paybackMonths,
        timestamp: new Date().toISOString()
      };

      const response = await apiFetch('/leads', {
        method: 'POST',
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('❌ [ROI CALCULATOR] Server error:', errorText);
        throw new Error('Failed to submit ROI calculator');
      }
      
      const result = await safeJsonParse(response);

      if (!result.success) {
        throw new Error(result.error || 'Failed to submit ROI calculator');
      }

      const pdfResult = await roiPageAPI.generatePDF({
        name: leadData.name,
        email: leadData.email,
        company: leadData.company,
        phone: leadData.phone,
        industry: formData.industry,
        numVideos: formData.numVideos,
        avgCostPerVideo: formData.avgCostPerVideo,
        employeesImpacted: formData.employeesImpacted,
        avgHourlyRate: formData.avgHourlyRate,
        hoursPerEmployeeSaved: formData.hoursPerEmployeeSaved,
        baselineCloseRate: formData.baselineCloseRate,
        salesLeadsImpacted: formData.salesLeadsImpacted,
        conversionLift: formData.conversionLift,
        avgDealSize: formData.avgDealSize,
        supportTicketsAvoided: formData.supportTicketsAvoided,
        avgTicketCost: formData.avgTicketCost,
        contentLifespan: formData.contentLifespan,
        totalInvestment,
        trainingSavings,
        salesRevenue,
        supportSavings,
        year1Benefits,
        year1ROI,
        year1ROIPercent: year1ROIPercent.toFixed(0),
        lifetimeValue,
        lifetimeROI,
        lifetimeROIPercent: lifetimeROIPercent.toFixed(0),
        paybackMonths
      });

      if (!pdfResult.success) {
        logger.error('⚠️ [ROI CALCULATOR] PDF generation failed:', pdfResult.error);
        setSuccessData({
          email: leadData.email,
          reportId: 'PENDING',
          name: leadData.name
        });
        setShowSuccessModal(true);
        setShowLeadForm(false);
        return;
      }

      if (pdfResult.success) {
        setSuccessData({
          email: leadData.email,
          reportId: pdfResult.data?.reportId || 'SUCCESS',
          name: leadData.name
        });
        setShowSuccessModal(true);
        setShowLeadForm(false);
      } else {
        logger.error('⚠️ [ROI CALCULATOR] PDF generation error:', pdfResult.error);
        setSuccessData({
          email: leadData.email,
          reportId: 'PENDING',
          name: leadData.name
        });
        setShowSuccessModal(true);
        setShowLeadForm(false);
      }
      
    } catch (error: any) {
      logger.error('❌ [ROI CALCULATOR] Error submitting ROI calculator:', error);
      alert('Failed to submit. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitLeadForm = (e: React.FormEvent) => {
    e.preventDefault();
    handleDownloadPDF();
  };

  // ============================================================
  // FIXED CALCULATIONS
  // ============================================================
  const calculations = useMemo(() => {
    const totalInvestment = formData.numVideos * formData.avgCostPerVideo;

    // -----------------------------------------------------------
    // 1. TRAINING SAVINGS
    // Formula: employees × hours saved × hourly rate
    // No artificial cap — user controls the inputs.
    // -----------------------------------------------------------
    const trainingSavings = formData.employeesImpacted * formData.hoursPerEmployeeSaved * formData.avgHourlyRate;

    // -----------------------------------------------------------
    // 2. SALES REVENUE — FIXED FORMULA
    //
    // OLD (wrong): leads × (conversionLift / 100) × dealSize
    //   → treated lift as an absolute rate, ignoring baseline
    //
    // NEW (correct): only count INCREMENTAL deals from the lift
    //   extraDeals = leads × baselineCloseRate × relativeConversionLift
    //   salesRevenue = extraDeals × avgDealSize
    //
    // Example: 120 leads, 8% baseline close rate, 15% relative lift
    //   → 120 × 0.08 × 0.15 = 1.44 extra deals × $95,000 = $136,800
    //   (NOT 120 × 0.15 × $95,000 = $1,710,000 as before)
    // -----------------------------------------------------------
    const baselineCloseRate = formData.baselineCloseRate / 100;
    const relativeConversionLift = formData.conversionLift / 100;
    const extraDeals = formData.salesLeadsImpacted * baselineCloseRate * relativeConversionLift;
    const salesRevenue = extraDeals * formData.avgDealSize;

    // -----------------------------------------------------------
    // 3. SUPPORT SAVINGS
    // Formula: tickets avoided × cost per ticket
    // No artificial cap — user controls the inputs.
    // -----------------------------------------------------------
    const supportSavings = formData.supportTicketsAvoided * formData.avgTicketCost;

    // -----------------------------------------------------------
    // 4. TOTALS & ROI
    // -----------------------------------------------------------
    const year1Benefits = trainingSavings + salesRevenue + supportSavings;
    const year1ROI = year1Benefits - totalInvestment;
    const year1ROIPercent = totalInvestment > 0 ? ((year1ROI / totalInvestment) * 100) : 0;

    // -----------------------------------------------------------
    // 5. LIFETIME VALUE — FIXED FORMULA
    //
    // OLD (wrong): year1Benefits × contentLifespan (flat multiplier)
    //   → assumed identical benefits every year, no decay
    //
    // NEW (correct): compounding 15% annual diminishing return
    //   Year 1: full value
    //   Year 2: × 0.85 (content ages, staff turnover, novelty wears off)
    //   Year 3: × 0.85² etc.
    //
    // This is still generous — real-world decay is often higher.
    // -----------------------------------------------------------
    const annualDecayRate = 0.15;
    let lifetimeValue = 0;
    for (let y = 0; y < formData.contentLifespan; y++) {
      lifetimeValue += year1Benefits * Math.pow(1 - annualDecayRate, y);
    }
    const lifetimeROI = lifetimeValue - totalInvestment;
    const lifetimeROIPercent = totalInvestment > 0 ? ((lifetimeROI / totalInvestment) * 100) : 0;

    // -----------------------------------------------------------
    // 6. PAYBACK PERIOD
    // -----------------------------------------------------------
    const monthlyBenefit = year1Benefits / 12;
    const paybackMonths = monthlyBenefit > 0 ? Math.ceil(totalInvestment / monthlyBenefit) : 0;

    return {
      totalInvestment,
      trainingSavings,
      salesRevenue,
      supportSavings,
      year1Benefits,
      year1ROI,
      year1ROIPercent,
      lifetimeValue,
      lifetimeROI,
      lifetimeROIPercent,
      paybackMonths,
      monthlyBenefit,
      extraDeals,
    };
  }, [formData]);
  
  const {
    totalInvestment,
    trainingSavings,
    salesRevenue,
    supportSavings,
    year1Benefits,
    year1ROI,
    year1ROIPercent,
    lifetimeValue,
    lifetimeROI,
    lifetimeROIPercent,
    paybackMonths,
    extraDeals,
  } = calculations;

  return (
    <div className="space-y-6">
      <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/60 overflow-hidden">
        
        <div className="p-6 sm:p-8 lg:p-10">
          {/* Industry Selection */}
          <div className="mb-8">
            <label className="block text-sm font-bold text-[#2F2E5C] mb-4 flex items-center gap-2 uppercase tracking-wide">
              <Target className="w-4 h-4 text-[#E87D3E]" />
              Select Your Industry
            </label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(industryPresets).map(([key, preset]) => (
                <button
                  key={key}
                  onClick={() => handleIndustryChange(key)}
                  className={`group relative px-4 py-2.5 rounded-full border-2 transition-all text-xs sm:text-sm font-semibold ${
                    formData.industry === key
                      ? 'border-[#E87D3E] bg-white text-[#E87D3E] shadow-md'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <span className="relative flex items-center gap-2">
                    {preset.name}
                    {formData.industry === key && (
                      <CheckCircle className="w-4 h-4 text-[#E87D3E]" />
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Core Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            <SliderField
              label="Number of Videos"
              value={formData.numVideos}
              onChange={(val) => handleInputChange('numVideos', val)}
              tooltip="How many videos do you plan to create?"
              proTip="Most clients start with 5-8 videos covering their core topics. This allows you to test effectiveness before scaling up. Consider: product demos, training modules, sales enablement, and customer onboarding."
              min={1}
              max={500}
              step={1}
            />
            
            <SliderField
              label="Cost per Video"
              value={formData.avgCostPerVideo}
              onChange={(val) => handleInputChange('avgCostPerVideo', val)}
              prefix="$"
              tooltip="Average production cost per video"
              proTip="Exact pricing depends on video length, complexity, and requirements. Our typical range: $2,500-$6,000 per video. Schedule a consultation for a custom quote based on your specific needs and industry requirements."
              min={1000}
              max={10000}
              step={250}
            />

            <SliderField
              label="Employees Impacted"
              value={formData.employeesImpacted}
              onChange={(val) => handleInputChange('employeesImpacted', val)}
              tooltip="Number of employees who will use these videos"
              proTip="Enter the number of employees who will ACTIVELY use the videos—not your total workforce. Industry average: 40-55% adoption rate. For example, if you have 300 employees, expect 120-165 active users in Year 1."
              min={0}
              max={10000}
              step={5}
            />

            <SliderField
              label="Average Hourly Rate"
              value={formData.avgHourlyRate}
              onChange={(val) => handleInputChange('avgHourlyRate', val)}
              prefix="$"
              suffix="/hr"
              tooltip="Average hourly cost per employee"
              proTip="Use LOADED cost (salary + benefits + overhead), not just base pay. Quick formula: Annual salary ÷ 2,000 hours × 1.4 (for benefits/overhead). Example: $80K salary = $40/hr × 1.4 = $56/hr loaded cost."
              min={25}
              max={250}
              step={5}
            />
          </div>

          {/* Advanced Options Toggle */}
          <div className="mb-6">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="group flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2.5 rounded-full border border-gray-200 hover:border-[#E87D3E] hover:shadow-md transition-all font-medium text-sm"
            >
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                showAdvanced ? 'border-[#E87D3E] bg-gradient-to-br from-[#E87D3E] to-[#E8B84D]' : 'border-gray-300 group-hover:border-[#E87D3E]'
              }`}>
                {showAdvanced ? (
                  <ChevronUp className="w-3 h-3 text-white" />
                ) : (
                  <ChevronDown className="w-3 h-3 text-gray-400 group-hover:text-[#E87D3E]" />
                )}
              </div>
              <span className="text-gray-700 group-hover:text-[#2F2E5C]">
                {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options for More Accuracy'}
              </span>
            </button>
          </div>

          {/* Advanced Inputs */}
          {showAdvanced && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8 p-6 bg-gradient-to-br from-gray-50/80 to-white/80 backdrop-blur-xl rounded-2xl border border-gray-200 shadow-inner">
              <div className="md:col-span-2 mb-2">
                <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#E87D3E]" />
                  Advanced Settings for Detailed ROI Analysis
                </h4>
              </div>
              
              <SliderField
                label="Hours Saved per Employee"
                value={formData.hoursPerEmployeeSaved}
                onChange={(val) => handleInputChange('hoursPerEmployeeSaved', val)}
                suffix="hrs"
                tooltip="Training time saved per employee per year"
                proTip="Be conservative. Typical savings: 1–5 hrs/yr for standard training videos, up to 40 hrs/yr for heavy onboarding programs. Only count hours saved vs. your prior training method, not total training time."
                min={0}
                max={500}
                step={0.5}
              />

              <SliderField
                label="Sales Leads Impacted"
                value={formData.salesLeadsImpacted}
                onChange={(val) => handleInputChange('salesLeadsImpacted', val)}
                tooltip="Leads who will interact with your videos"
                proTip="Count leads who will VIEW your videos (on website, in email campaigns, during sales demos). Don't include every lead in your database—only those who will actually engage with video content. Typical B2B: 30-50% of leads watch product videos."
                min={10}
                max={1000000}
                step={10}
              />

              <SliderField
                label="Baseline Close Rate"
                value={formData.baselineCloseRate}
                onChange={(val) => handleInputChange('baselineCloseRate', val)}
                suffix="%"
                tooltip="Your current conversion rate before video"
                proTip="Your actual close rate from CRM data — the % of leads that convert today without video. This anchors the sales calculation so video only gets credit for the INCREMENTAL improvement it causes on top of your existing performance."
                min={1}
                max={60}
                step={1}
              />

              <SliderField
                label="Conversion Lift (relative %)"
                value={formData.conversionLift}
                onChange={(val) => handleInputChange('conversionLift', val)}
                suffix="%"
                tooltip="Relative improvement on your baseline close rate"
                proTip="This is a RELATIVE improvement on your baseline close rate — not an absolute rate. Example: if you close 8% today and video adds 15% relative lift, your new rate is 8% × 1.15 = 9.2%. Industry data (Wistia/Forrester): typical B2B video lift is 10–25% relative."
                min={1}
                max={100}
                step={0.5}
              />

              <SliderField
                label="Average Deal Size"
                value={formData.avgDealSize}
                onChange={(val) => handleInputChange('avgDealSize', val)}
                prefix="$"
                tooltip="Average revenue per closed deal"
                proTip="Use your actual average deal value from CRM data. Include only revenue from closed deals—not pipeline or potential value. For multi-product companies, calculate a weighted average across product lines."
                min={1}
                max={1000000}
                step={1}
              />

              <SliderField
                label="Support Tickets Avoided"
                value={formData.supportTicketsAvoided}
                onChange={(val) => handleInputChange('supportTicketsAvoided', val)}
                tooltip="Tickets prevented through video content per year"
                proTip="Only count tickets avoided by DOCUMENTATION/TRAINING videos. Formula: (Current annual tickets × % that are how-to questions) × 30–40% reduction. Example: 200 tickets, 50% how-to = 100 × 35% = 35 avoided/yr."
                min={0}
                max={1000000}
                step={10}
              />

              <SliderField
                label="Cost per Ticket"
                value={formData.avgTicketCost}
                onChange={(val) => handleInputChange('avgTicketCost', val)}
                prefix="$"
                tooltip="Average cost to resolve a support ticket"
                proTip="Include: support rep time (avg 30–45 min), follow-up communication, tools/software costs, and management overhead. Quick estimate: Annual support team cost ÷ Annual tickets resolved. Industry average: $85–125 per ticket for B2B technical support."
                min={1}
                max={500}
                step={1}
              />

              <SliderField
                label="Content Lifespan"
                value={formData.contentLifespan}
                onChange={(val) => handleInputChange('contentLifespan', val)}
                suffix="years"
                tooltip="How long the videos will remain relevant"
                proTip="How long before content needs major updates? Fast-moving industries (tech, automotive): 2–3 years. Stable industries (manufacturing, aerospace): 3–5 years. Don't overestimate—plan for refreshes when products, processes, or regulations change."
                min={1}
                max={15}
                step={0.5}
              />
            </div>
          )}

          {/* Results Dashboard */}
          <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/60 shadow-xl mb-6 overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#E87D3E]/10 to-transparent rounded-full blur-2xl" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-[#E8B84D]/10 to-transparent rounded-full blur-2xl" />
            
            <div className="relative z-10">
              <h4 className="text-base sm:text-lg font-bold text-[#2F2E5C] mb-4 sm:mb-6 flex items-center gap-2">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-xl flex items-center justify-center shadow-lg">
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                Your Investment Results
              </h4>

              {/* Key Metrics Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
                {/* Total Investment */}
                <div className="group relative bg-white/60 backdrop-blur-sm rounded-2xl p-4 sm:p-5 border border-white/60 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-[#2F2E5C] to-[#4a487a] rounded-lg flex items-center justify-center">
                      <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                    </div>
                    <div className="text-xs font-bold text-gray-600">Total Investment</div>
                  </div>
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#2F2E5C] mb-1">
                    ${totalInvestment.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500">{formData.numVideos} videos</div>
                </div>

                {/* Year 1 ROI - Hero Card */}
                <div className="group relative bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-2xl p-4 sm:p-5 shadow-xl transform sm:scale-105 sm:hover:scale-110 transition-all overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12" />
                  <div className="absolute bottom-0 left-0 w-20 h-20 bg-black/5 rounded-full -ml-10 -mb-10" />
                  
                  <div className="relative flex items-center gap-2 mb-2 sm:mb-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                    </div>
                    <div className="text-xs font-bold text-white/90">Year 1 ROI</div>
                  </div>
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white relative mb-2">
                    ${year1ROI.toLocaleString()}
                  </div>
                  <div className="flex items-center gap-2 relative">
                    <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                    <div className="text-xs sm:text-sm font-bold text-white">
                      {year1ROIPercent.toFixed(0)}% Return
                    </div>
                  </div>
                </div>

                {/* Lifetime Value */}
                <div className="group relative bg-white/60 backdrop-blur-sm rounded-2xl p-4 sm:p-5 border border-white/60 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-[#2F2E5C] to-[#4a487a] rounded-lg flex items-center justify-center">
                      <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                    </div>
                    <div className="text-xs font-bold text-gray-600">Lifetime ROI</div>
                  </div>
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent mb-1">
                    ${lifetimeROI.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500">Over {formData.contentLifespan} years</div>
                </div>
              </div>
              
              {/* Disclaimer */}
              <div className="mt-4 mb-4 bg-amber-50/50 backdrop-blur-sm border border-amber-200/50 rounded-xl p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  <strong className="font-bold">Based on strategic implementation and organizational adoption.</strong> Actual results depend on execution quality and may vary.
                </p>
              </div>

              {/* Payback Period */}
              {paybackMonths > 0 && (
                <div className="relative bg-white/60 backdrop-blur-xl rounded-2xl p-4 sm:p-5 border border-emerald-200 shadow-md mb-6 overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-full blur-2xl" />
                  <div className="relative flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs sm:text-sm font-bold text-emerald-900 mb-1">
                        Payback Period: {paybackMonths} month{paybackMonths !== 1 ? 's' : ''}
                      </div>
                      <div className="text-xs text-emerald-700">
                        Your investment will pay for itself in {paybackMonths < 6 ? 'under 6 months' : `${paybackMonths} months`}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Benefits Breakdown */}
              <div className="space-y-3">
                <div className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                  <div className="w-1 h-5 bg-gradient-to-b from-[#E87D3E] to-[#E8B84D] rounded-full" />
                  Benefits Breakdown (Year 1)
                </div>
                
                {/* Training */}
                <div className="group relative bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-blue-100 shadow-sm hover:shadow-md transition-all overflow-hidden">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/5 rounded-full blur-xl" />
                  <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-sm text-gray-900 mb-0.5">Training Time Savings</div>
                        <div className="text-xs text-gray-600">{formData.employeesImpacted} employees × {formData.hoursPerEmployeeSaved} hrs saved × ${formData.avgHourlyRate}/hr</div>
                      </div>
                    </div>
                    <div className="font-bold text-base sm:text-lg md:text-xl text-blue-600 flex-shrink-0">
                      ${trainingSavings.toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Sales */}
                <div className="group relative bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-purple-100 shadow-sm hover:shadow-md transition-all overflow-hidden">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-purple-500/5 rounded-full blur-xl" />
                  <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                        <TrendingUp className="w-5 h-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-sm text-gray-900 mb-0.5">Incremental Sales Revenue</div>
                        <div className="text-xs text-gray-600">
                          {formData.salesLeadsImpacted.toLocaleString()} leads × {formData.baselineCloseRate}% close rate × {formData.conversionLift}% relative lift = {extraDeals < 1 ? extraDeals.toFixed(2) : extraDeals.toFixed(1)} extra deals
                        </div>
                      </div>
                    </div>
                    <div className="font-bold text-base sm:text-lg md:text-xl text-purple-600 flex-shrink-0">
                      ${salesRevenue.toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Support */}
                <div className="group relative bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-orange-100 shadow-sm hover:shadow-md transition-all overflow-hidden">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-orange-500/5 rounded-full blur-xl" />
                  <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                        <Target className="w-5 h-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-sm text-gray-900 mb-0.5">Support Cost Savings</div>
                        <div className="text-xs text-gray-600">{formData.supportTicketsAvoided.toLocaleString()} tickets avoided × ${formData.avgTicketCost}/ticket</div>
                      </div>
                    </div>
                    <div className="font-bold text-base sm:text-lg md:text-xl text-orange-600 flex-shrink-0">
                      ${supportSavings.toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Total */}
                <div className="relative pt-5 mt-4">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-white/60 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#2F2E5C] to-[#4a487a] rounded-xl flex items-center justify-center shadow-md">
                          <BarChart3 className="w-5 h-5 text-white" />
                        </div>
                        <div className="font-bold text-base text-gray-900">Total Annual Benefits</div>
                      </div>
                      <div className="font-bold text-2xl sm:text-3xl bg-gradient-to-r from-[#2F2E5C] to-[#4a487a] bg-clip-text text-transparent">
                        ${year1Benefits.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Success Factors Framework */}
          <div className="bg-gradient-to-br from-blue-50/80 to-white/80 backdrop-blur-xl rounded-2xl p-6 sm:p-7 border-2 border-blue-200/60 shadow-lg mb-6 overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-2xl" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#2F2E5C]/5 rounded-full blur-2xl" />
            
            <div className="relative">
              <div className="flex items-start gap-3 mb-4 sm:mb-5">
                <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Target className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-base sm:text-lg font-bold text-[#2F2E5C] mb-1">How to Achieve These Results</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Videos are just one piece of the puzzle. To reach this ROI, you'll need:
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-blue-100 shadow-sm">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md" style={{ background: 'linear-gradient(135deg, #E87D3E 0%, #E8B84D 100%)' }}>
                      <Video className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-sm font-bold text-[#2F2E5C] mb-1">1. Strategic Video Planning</h5>
                      <p className="text-xs text-gray-600 mb-2">Professional videos aligned with business objectives</p>
                    </div>
                  </div>
                  <ul className="space-y-1.5 ml-3 sm:ml-12">
                    <li className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Clear objectives for each video</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Alignment with business goals</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Targeted to specific audiences</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-blue-100 shadow-sm">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md" style={{ background: 'linear-gradient(135deg, #9333ea 0%, #7e22ce 100%)' }}>
                      <Megaphone className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-sm font-bold text-[#2F2E5C] mb-1">2. Effective Distribution</h5>
                      <p className="text-xs text-gray-600 mb-2">Multi-channel promotion and integration</p>
                    </div>
                  </div>
                  <ul className="space-y-1.5 ml-3 sm:ml-12">
                    <li className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle className="w-3.5 h-3.5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Integration into your LMS/CRM systems</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle className="w-3.5 h-3.5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Sales team enablement and training</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle className="w-3.5 h-3.5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Multi-channel promotion strategy</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-blue-100 shadow-sm">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md" style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}>
                      <UserCheck className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-sm font-bold text-[#2F2E5C] mb-1">3. Organizational Adoption</h5>
                      <p className="text-xs text-gray-600 mb-2">Change management and performance tracking</p>
                    </div>
                  </div>
                  <ul className="space-y-1.5 ml-3 sm:ml-12">
                    <li className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>Executive sponsorship and endorsement</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>Change management and training programs</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>Regular performance measurement</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-5 bg-gradient-to-r from-[#2F2E5C]/10 to-blue-600/10 rounded-xl p-4 border border-[#2F2E5C]/20">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-[#2F2E5C] to-[#4a487a] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-bold text-[#2F2E5C] mb-1">Key Insight</p>
                    <p className="text-xs text-gray-700 leading-relaxed">
                      Our clients who achieve <strong>150%+ ROI</strong> typically invest in our full implementation program, not just video production. 
                      <strong className="text-[#2F2E5C]"> Industry benchmark: 70-85% of projected ROI in Year 1</strong> with professional implementation support.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center pt-2">
            <p className="text-xs sm:text-sm text-gray-600 mb-4 px-2">
              Get your personalized <strong>implementation roadmap</strong> showing exactly how to achieve this ROI
            </p>
            <Button
              onClick={() => setShowLeadForm(true)}
              disabled={isSubmitting}
              className="bg-gradient-to-r from-[#2F2E5C] to-[#4A567B] hover:from-[#4A567B] hover:to-[#2F2E5C] text-white rounded-full px-6 sm:px-8 h-11 sm:h-12 gap-2 shadow-lg text-sm sm:text-base"
              variant={undefined}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="hidden sm:inline">Generating...</span>
                  <span className="sm:hidden">Please wait...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Get Implementation Roadmap + ROI Report</span>
                  <span className="sm:hidden">Get ROI Report</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Lead Capture Modal */}
      {showLeadForm && createPortal(
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
          onClick={() => setShowLeadForm(false)}
        >
          <div className="fixed inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-bl from-[#E8B84D]/30 via-[#E87D3E]/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
            <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-gradient-to-tr from-[#D74444]/25 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-br from-[#2F2E5C]/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
          </div>

          <div 
            className="relative bg-white/95 backdrop-blur-2xl rounded-2xl sm:rounded-3xl max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-300 my-auto border-2 border-white/40"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />
            <div className="absolute -inset-1 bg-gradient-to-br from-[#2F2E5C]/20 via-[#4a487a]/20 to-[#E87D3E]/20 rounded-2xl sm:rounded-3xl blur-xl -z-10" />

            <button
              onClick={() => setShowLeadForm(false)}
              className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-xl hover:bg-white transition-all z-10 group shadow-lg border border-gray-200"
            >
              <X className="w-5 h-5 text-gray-600 group-hover:text-gray-900 group-hover:rotate-90 transition-all" />
            </button>

            <div className="relative p-5 sm:p-6 border-b border-gray-200/50">
              <div className="flex items-start gap-3">
                <div className="relative flex-shrink-0">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#2F2E5C] to-[#4a487a] rounded-xl blur-lg opacity-40" />
                  <div className="relative w-11 h-11 bg-gradient-to-br from-[#2F2E5C] to-[#4a487a] rounded-xl flex items-center justify-center shadow-xl">
                    <Download className="w-6 h-6 text-white" />
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-bold text-[#2F2E5C] mb-1">Get Your ROI Report</h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                    Enter your details to receive a comprehensive PDF with detailed ROI analysis and expert recommendations.
                  </p>
                </div>
              </div>
            </div>
            
            <form onSubmit={submitLeadForm} className="relative p-5 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Full Name *</label>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#2F2E5C]/20 to-[#4a487a]/20 rounded-lg blur-md opacity-0 group-focus-within:opacity-100 transition-opacity" />
                  <Input
                    type="text"
                    required
                    value={leadData.name}
                    onChange={(e) => handleLeadInputChange('name', e.target.value)}
                    placeholder="John Smith"
                    className="relative h-11 rounded-lg border-2 border-gray-200 focus:border-[#2F2E5C] focus:ring-2 focus:ring-[#2F2E5C]/20 transition-all bg-white/80 backdrop-blur-sm text-sm px-4"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Company Name *</label>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#2F2E5C]/20 to-[#4a487a]/20 rounded-lg blur-md opacity-0 group-focus-within:opacity-100 transition-opacity" />
                  <Input
                    type="text"
                    required
                    value={leadData.company}
                    onChange={(e) => handleLeadInputChange('company', e.target.value)}
                    placeholder="Acme Manufacturing"
                    className="relative h-11 rounded-lg border-2 border-gray-200 focus:border-[#2F2E5C] focus:ring-2 focus:ring-[#2F2E5C]/20 transition-all bg-white/80 backdrop-blur-sm text-sm px-4"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Work Email *</label>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#2F2E5C]/20 to-[#4a487a]/20 rounded-lg blur-md opacity-0 group-focus-within:opacity-100 transition-opacity" />
                  <Input
                    type="email"
                    required
                    value={leadData.email}
                    onChange={(e) => handleLeadInputChange('email', e.target.value)}
                    placeholder="john@acme.com"
                    className="relative h-11 rounded-lg border-2 border-gray-200 focus:border-[#2F2E5C] focus:ring-2 focus:ring-[#2F2E5C]/20 transition-all bg-white/80 backdrop-blur-sm text-sm px-4"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Phone Number <span className="text-gray-500 font-normal">(Optional)</span>
                </label>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#2F2E5C]/20 to-[#4a487a]/20 rounded-lg blur-md opacity-0 group-focus-within:opacity-100 transition-opacity" />
                  <Input
                    type="tel"
                    value={leadData.phone}
                    onChange={(e) => handleLeadInputChange('phone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="relative h-11 rounded-lg border-2 border-gray-200 focus:border-[#2F2E5C] focus:ring-2 focus:ring-[#2F2E5C]/20 transition-all bg-white/80 backdrop-blur-sm text-sm px-4"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group/btn relative w-full overflow-hidden h-11 order-1"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#4A567B] to-[#2F2E5C] rounded-full blur-lg opacity-50 group-hover/btn:opacity-75 transition-opacity" />
                  <div className="relative bg-gradient-to-r from-[#2F2E5C] to-[#4A567B] hover:from-[#4A567B] hover:to-[#2F2E5C] text-white h-full rounded-full transition-all shadow-xl flex items-center justify-center gap-2">
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span className="font-bold text-sm">Generating...</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 group-hover/btn:translate-y-0.5 transition-transform" />
                        <span className="font-bold text-sm">Download Report</span>
                      </>
                    )}
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setShowLeadForm(false)}
                  className="w-full rounded-full border-2 border-gray-300 hover:border-gray-400 h-11 text-sm font-semibold hover:bg-gray-50 transition-all text-gray-700 bg-white order-2"
                >
                  Cancel
                </button>
              </div>

              <p className="text-xs sm:text-sm text-gray-500 text-center leading-relaxed pt-1">
                Your information is secure. We'll only use it to send your report.
              </p>
            </form>
          </div>
        </div>,
        typeof document !== 'undefined' && document.body ? document.body : document.createElement('div')
      )}

      {/* Success Modal */}
      {showSuccessModal && successData && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-in zoom-in duration-300">
            <button
              onClick={() => setShowSuccessModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] flex items-center justify-center shadow-lg">
                <CheckCircle className="w-8 h-8 text-white" strokeWidth={2.5} />
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#2F2E5C] mb-3">
                Success! Your Report is On Its Way
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Thank you, <strong className="text-[#E87D3E]">{successData.name}</strong>! We've sent your personalized ROI report to <strong className="text-[#E87D3E]">{successData.email}</strong>
              </p>

              {successData.reportId !== 'PENDING' && (
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <div className="text-xs text-gray-500 mb-1">Report ID</div>
                  <div className="text-sm font-mono text-gray-700 break-all">{successData.reportId}</div>
                </div>
              )}

              <div className="bg-gradient-to-r from-[#E87D3E]/10 to-[#E8B84D]/10 rounded-xl p-5 mb-6 border border-[#E87D3E]/20">
                <h4 className="text-sm font-bold text-[#2F2E5C] mb-3">What happens next?</h4>
                <div className="space-y-3 text-left">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] flex items-center justify-center text-white text-xs font-bold">1</div>
                    <p className="text-sm text-gray-600">Check your inbox for the detailed PDF report</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] flex items-center justify-center text-white text-xs font-bold">2</div>
                    <p className="text-sm text-gray-600">Review your ROI breakdown and recommendations</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] flex items-center justify-center text-white text-xs font-bold">3</div>
                    <p className="text-sm text-gray-600">Our team will follow up within 24 hours</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => setShowSuccessModal(false)}
                className="w-full bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] hover:from-[#E8B84D] hover:to-[#E87D3E] text-white rounded-full h-12 text-sm font-bold shadow-lg hover:shadow-xl transition-all"
              >
                Got it, thanks!
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        .premium-slider::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #E87D3E, #E8B84D);
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(232, 125, 62, 0.4);
          transition: all 0.3s;
          border: 3px solid white;
        }
        .premium-slider::-webkit-slider-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 6px 16px rgba(232, 125, 62, 0.5);
        }
        .premium-slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #E87D3E, #E8B84D);
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 4px 12px rgba(232, 125, 62, 0.4);
          transition: all 0.3s;
        }
        .premium-slider::-moz-range-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 6px 16px rgba(232, 125, 62, 0.5);
        }
      `}</style>
    </div>
  );
}